import React, { useState, useEffect } from 'react';
import { getTimeline } from '../services/api';
import TimelineNode from './TimelineNode';
import DetailModal from './DetailModal';

const InteractiveTimeline = () => {
  const [timelineData, setTimelineData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [hoveredTrack, setHoveredTrack] = useState(null);

  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        const data = await getTimeline();
        setTimelineData(data);
      } catch (error) {
        console.error('Error fetching timeline:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTimeline();
  }, []);

  const handleNodeClick = (event, track) => {
    setSelectedEvent({ ...event, track });
  };

  const trackColors = {
    professional: {
      primary: 'from-slate-700 to-slate-900',
      border: 'border-slate-600',
      bg: 'bg-slate-700',
      hover: 'hover:border-slate-500',
      text: 'text-slate-700',
      glow: 'shadow-slate-500/30'
    },
    investments: {
      primary: 'from-amber-500 to-amber-600',
      border: 'border-amber-500',
      bg: 'bg-amber-500',
      hover: 'hover:border-amber-400',
      text: 'text-amber-600',
      glow: 'shadow-amber-500/30'
    },
    personal: {
      primary: 'from-rose-400 to-rose-500',
      border: 'border-rose-400',
      bg: 'bg-rose-400',
      hover: 'hover:border-rose-300',
      text: 'text-rose-500',
      glow: 'shadow-rose-400/30'
    }
  };

  // Get all unique years from all tracks
  const allYears = timelineData ? [...new Set([
    ...timelineData.professional.map(e => e.year),
    ...timelineData.investments.map(e => e.year),
    ...timelineData.personal.map(e => e.year)
  ])].sort((a, b) => parseInt(a) - parseInt(b)) : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-white py-24 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-slate-700 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-slate-600">Loading timeline...</p>
        </div>
      </div>
    );
  }

  if (!timelineData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-white py-24 flex items-center justify-center">
        <p className="text-slate-600">Unable to load timeline</p>
      </div>
    );
  }

  return (
    <div id="timeline" className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-white py-16 md:py-24 relative overflow-hidden">
      {/* Decorative background elements - muted executive tones */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-br from-slate-300 to-slate-400 rounded-full blur-3xl" />
        <div className="absolute top-40 right-10 w-96 h-96 bg-gradient-to-br from-amber-200 to-amber-300 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-1/2 w-80 h-80 bg-gradient-to-br from-rose-200 to-rose-300 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 md:px-12 relative z-10">
        {/* Section Header */}
        <div className="max-w-4xl mx-auto text-center mb-12 md:mb-20">
          <div className="inline-block mb-4 md:mb-6">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-slate-600 animate-pulse" />
              <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" style={{ animationDelay: '0.2s' }} />
              <div className="w-2 h-2 rounded-full bg-rose-400 animate-pulse" style={{ animationDelay: '0.4s' }} />
            </div>
          </div>
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-4 md:mb-6 text-slate-800">
            My Journey
          </h2>
          <p className="text-base md:text-xl text-slate-600 font-light leading-relaxed px-4">
            Three parallel streams flowing through time — each representing a different path of growth.
            <br />
            <span className="text-sm md:text-base mt-2 md:mt-3 block text-slate-500 italic">Click any milestone to dive deeper into the story</span>
          </p>
        </div>

        {/* Timeline Container with Years on Left */}
        <div className="relative max-w-7xl mx-auto xl:flex xl:gap-8">
          {/* Years Column on Left - Desktop only (xl and above) */}
          <div className="hidden xl:block w-28 flex-shrink-0">
            {/* Spacer to align years with first event row (below headers) */}
            <div className="h-44" />
            <div className="space-y-[7.5rem]">
              {allYears.map((year, index) => (
                <div key={year} className="relative">
                  <h3 className="text-3xl font-bold bg-gradient-to-br from-gray-900 to-gray-600 bg-clip-text text-transparent">
                    {year}
                  </h3>
                  <div className="mt-2 h-1 w-10 bg-gradient-to-r from-gray-400 to-transparent" />
                  {index < allYears.length - 1 && (
                    <div className="absolute top-14 left-5 w-px h-20 bg-gradient-to-b from-gray-300 to-transparent" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Three Column Timeline */}
          <div className="flex-1">
            {/* Track Labels with gradient underlines */}
            <div className="grid grid-cols-3 gap-2 md:gap-8 mb-8 md:mb-12">
              <div 
                className={`text-center pb-3 md:pb-6 border-b-4 border-slate-600 transition-all duration-300 cursor-pointer transform ${
                  hoveredTrack === 'professional' ? 'scale-105 shadow-lg shadow-slate-500/20' : ''
                }`}
                onMouseEnter={() => setHoveredTrack('professional')}
                onMouseLeave={() => setHoveredTrack(null)}
              >
                <div className="inline-block p-2 md:p-3 bg-gradient-to-br from-slate-700 to-slate-900 rounded-xl md:rounded-2xl mb-2 md:mb-3 shadow-lg shadow-slate-500/30">
                  <svg className="w-5 h-5 md:w-8 md:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-sm md:text-2xl font-bold text-slate-800">Professional</h3>
                <p className="hidden md:block text-sm text-slate-500 mt-1">Career & Technical Growth</p>
              </div>
              <div 
                className={`text-center pb-3 md:pb-6 border-b-4 border-amber-500 transition-all duration-300 cursor-pointer transform ${
                  hoveredTrack === 'investments' ? 'scale-105 shadow-lg shadow-amber-500/20' : ''
                }`}
                onMouseEnter={() => setHoveredTrack('investments')}
                onMouseLeave={() => setHoveredTrack(null)}
              >
                <div className="inline-block p-2 md:p-3 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl md:rounded-2xl mb-2 md:mb-3 shadow-lg shadow-amber-500/30">
                  <svg className="w-5 h-5 md:w-8 md:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <h3 className="text-sm md:text-2xl font-bold text-slate-800">Investments</h3>
                <p className="hidden md:block text-sm text-slate-500 mt-1">Venture Portfolio</p>
              </div>
              <div 
                className={`text-center pb-3 md:pb-6 border-b-4 border-rose-400 transition-all duration-300 cursor-pointer transform ${
                  hoveredTrack === 'personal' ? 'scale-105 shadow-lg shadow-rose-400/20' : ''
                }`}
                onMouseEnter={() => setHoveredTrack('personal')}
                onMouseLeave={() => setHoveredTrack(null)}
              >
                <div className="inline-block p-2 md:p-3 bg-gradient-to-br from-rose-400 to-rose-500 rounded-xl md:rounded-2xl mb-2 md:mb-3 shadow-lg shadow-rose-400/30">
                  <svg className="w-5 h-5 md:w-8 md:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="text-sm md:text-2xl font-bold text-slate-800">Personal</h3>
                <p className="hidden md:block text-sm text-slate-500 mt-1">Life Milestones</p>
              </div>
            </div>

            {/* Flowing Timeline */}
            <div className="relative">
              {/* Vertical lines for each track - hidden on mobile */}
              <div className="hidden md:grid absolute inset-0 grid-cols-3 gap-8 pointer-events-none">
                <div className="flex justify-center">
                  <div className="w-px bg-gradient-to-b from-slate-300 via-slate-200 to-transparent opacity-40" />
                </div>
                <div className="flex justify-center">
                  <div className="w-px bg-gradient-to-b from-amber-300 via-amber-200 to-transparent opacity-40" />
                </div>
                <div className="flex justify-center">
                  <div className="w-px bg-gradient-to-b from-rose-300 via-rose-200 to-transparent opacity-40" />
                </div>
              </div>

              {/* Timeline Events */}
              <div className="grid grid-cols-3 gap-2 md:gap-8 relative">
                {/* Professional Track */}
                <div className="space-y-4 md:space-y-8">
                  {timelineData.professional.map((event, index) => (
                    <TimelineNode
                      key={event._id || index}
                      event={event}
                      track="professional"
                      onClick={() => handleNodeClick(event, 'professional')}
                      colors={trackColors.professional}
                      delay={index * 150}
                    />
                  ))}
                </div>

                {/* Investment Track */}
                <div className="space-y-4 md:space-y-8 pt-8 md:pt-16">
                  {timelineData.investments.map((event, index) => (
                    <TimelineNode
                      key={event._id || index}
                      event={event}
                      track="investments"
                      onClick={() => handleNodeClick(event, 'investments')}
                      colors={trackColors.investments}
                      delay={index * 150 + 75}
                    />
                  ))}
                </div>

                {/* Personal Track */}
                <div className="space-y-4 md:space-y-8 pt-16 md:pt-32">
                  {timelineData.personal.map((event, index) => (
                    <TimelineNode
                      key={event._id || index}
                      event={event}
                      track="personal"
                      onClick={() => handleNodeClick(event, 'personal')}
                      colors={trackColors.personal}
                      delay={index * 150 + 150}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedEvent && (
        <DetailModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </div>
  );
};

export default InteractiveTimeline;
